import { Accessor, ColorX, GFunc, LocaleX, LogicX, ReqX, ZMarkdown, ZSearch, ZValidate } from "IZOX/STATIC";
import { HStack, VStack, XButton, XDiv, XScroll, XText } from "IZOX/_X";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import QFText from "./QFText";
import _ from "lodash";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import Markdown from "../../Markdown/Markdown";
import { FaCheck } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { v1 } from "uuid";
import OptionList from "./_parts/OptionList";

/**
 * 
 * @typedef {import("./QFDropdown").qfdropdownparams} qfdropdownparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * 
 * @typedef {Object} qffreetexttagaddons
 * @property {String} FTGID
 * @property {String} GetAPI
 * @property {String} AddAPI
 * @property {String} DeleteAPI
 * @property {Boolean} canAdd
 * @property {Boolean} canDelete
 * 
 * @typedef {qfdropdownparams & qffreetexttagaddons} qffreetexttagparams
 * 
 * @param {qffreetexttagparams} param0
 * @returns 
 */
const QFFreeTextTag = ({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  btnColor = undefined,
  error_bColor = ColorX.CSS("Error"),
  invalidColor = ColorX.CSS("pureRed"),
  disabledColor = ColorX.CSS("greyOut"),
  disabled_bgColor = undefined,
  renderOption = undefined,
  listMaxHeight = 300,
  listFontSize = 14,
  popupPosition = "bottom",
  iconSize = undefined,
  allowSearch = true,
  searchComparer = null,
  capAccessor = "",
  valAccessor = "",
  colorAccessor = "color",
  bgColorAccessor = "bgColor",
  disableAccessor = "disabled",
  hideIfDisabled = false,
  btnStyle = {},
  containerStyle = {},
  qfTextStyle = {},
  arrowColor = ColorX.CSS("placeholder"),
  iconWidth = 50,
  onSelectChange = null,
  isError = undefined,
  addOns = undefined,
  displayAlign = "[",
  updateVia = undefined,
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  multiple = false,
  closeOnSelect = true,
  breakLines = false,
  FTGID = "",
  GetAPI = "/CommonAPI/__FTG__/GetOptions",
  AddAPI = "/CommonAPI/__FTG__/AddOption",
  DeleteAPI = "/CommonAPI/__FTG__/DeleteOption",
  canAdd = true,
  canDelete = true,
  validators = [],
  popupRound = 5,
  showBullets = false,
  bulletHeight = 80,
  ...others
}) => {

  const ref = useRef(null);
  const [display, setDisplay] = useState(defaultValue);
  const [valid, setValid] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const textRef = useRef(null);
  const bulletRef = useRef(null);
  const opctrls = useRef(null);

  const GetOptions = useCallback(async () => {
    let res = await ReqX.SendBE(GetAPI, {
      docID: FTGID
    });
    let {Success, payload} = res;
    if(Success){
      setOptions(payload);
    }
  }, [FTGID, GetAPI]);

  useEffect(() => {
    GetOptions();
  }, [GetOptions]);

  const AddOption = async (option) => {
    let res = await ReqX.SendBE(AddAPI, {
      docID: FTGID,
      option: option
    });
    let {Success} = res;
    if(Success){
      GetOptions();
      onListConfirm(option);
    }
  };

  const DeleteOption = async (option) => {
    let res = await ReqX.SendBE(DeleteAPI, {
      docID: FTGID,
      option: option
    });
    let {Success} = res;
    if(Success){
      if(multiple){
        let _value = Accessor.Get(formValue, name) ?? [];
        if(_value?.includes(option)){
          let newValue = _.filter(_value, o => o !== option);
          _onChange(name, newValue);
        }
      }else{
        let _value = Accessor.Get(formValue, name);
        if(_value === option){
          _onChange(name, undefined);
        }
      }
      GetOptions();
      openList("");
    }
  };

  const GetDis = (_option) => {
    if(multiple){
      return _.map(_option, o => LocaleX.Parse(
        Accessor.Get(o, capAccessor) ?? 
        Accessor.Get(o, valAccessor) ?? "")).join(", ");
    }
    return LocaleX.Parse(
      Accessor.Get(_option, capAccessor) ?? 
      Accessor.Get(_option, valAccessor) ?? "");
  };

  const FindOption = useCallback((_value) => {
    if(multiple){
      return _.filter(options, o => _value?.includes(Accessor.Get(o, valAccessor)));
    }
    return _.find(options, o => _value === Accessor.Get(o, valAccessor));
  }, [options, valAccessor, multiple]);

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(name, defaultValue);
      let _option = FindOption(defaultValue);
      let disStr = "";
      if(_option){
        disStr = GetDis(_option);
        setValid(true);
      }else{
        setValid(false);
      }
      setDisplay(disStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line no-unused-vars
  let {bgColor, bW, bColor, color, fontSize, readOnly, round, p, ...exOthers} = others;

  // const onInputChange = (name, text) => {
  //   setDisplay(text);
  // };

  const updateList = useCallback((newForm, _name, newValue) => {
    if(!closeOnSelect){
      opctrls?.current?.setValue?.(newValue);
    }else{
      setShowSearch(false);
      GFunc.CloseSelectorOverlay();
    }
    setTimeout(() => bulletRef.current?.scrollToBottom?.(), 10);
  }, [closeOnSelect]);

  const onListConfirm = (value, _newForm = undefined) => {
    if(readOnly) return;
    if(_onChange){
      _onChange(name, (_value = []) => {
        if(!multiple) {
          setTimeout(() => onSelectChange?.(value), 0);
          return value;
        }
        let newValue = value;
        if(_value.includes(value)){
          newValue = _.filter(_value, o => o !== value);
        }else{
          newValue = [..._value, value];
        }
        setTimeout(() => onSelectChange?.(newValue), 0);
        return newValue;
      }, [], updateList);
    }else{
      setTimeout(() => onSelectChange?.(value), 0);
      updateList();
    }
    setValid(true);
  };

  const _searchChange = (name, value) => {
    setSearchText(value);
    GFunc.CloseSelectorOverlay();
    openList(value);
  };

  useEffect(() => {
    let _value = Accessor.Get(formValue, name);
    if(_.isUndefined(_value)){
      setValid(true);
      setDisplay("");
      return;
    }
    let _option = FindOption(_value);
    let disStr = "";
    if(_option){
      disStr = GetDis(_option);
      setValid(true);
    }else{
      setValid(false);
    }
    setDisplay(disStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue, name, options]);

  const _renderOption = (option, isSelected, _searchText = "", _newForm = undefined, _isHighlighted = false) => {
    let cap = Accessor.Get(option, capAccessor);
    let val = Accessor.Get(option, valAccessor);
    let _color = Accessor.Get(option, colorAccessor) ?? color;
    let _bgColor = Accessor.Get(option, bgColorAccessor) ?? bgColor;
    let _disabled = Accessor.Get(option, disableAccessor) ?? false;
    let str = LocaleX.Parse(cap);
    if(showSearch && !_.isEmpty(_searchText)){
      str = ZMarkdown.BoldMatched(str, _searchText);
      return (
        <HStack key={v1()}>
          <XButton key={val} padding={5} round={0} colorTransition
            opacity={_disabled ? 0.3 : 1}
            fullWidth bgColor={ColorX.CSS(isSelected? "aliceblue" : (_bgColor ?? bgColor ?? "pureWhite"))}
            bW={_isHighlighted ? 2 : 0} bColor={_isHighlighted ? ColorX.CSS("cmBlue") : undefined}
            onPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onListConfirm(val, _newForm);
            }} disabled={_disabled}>
            {renderOption?.(option, isSelected, _searchText) ?? 
              <HStack>
                <Markdown width={"100%"} style={{ 
                  width: "100%", 
                  fontSize: listFontSize, 
                  textAlign: "center",
                  color: _color,
                  flexGrow: 1
                }}>
                  {str}
                </Markdown>
                {multiple && 
                  <XDiv width={20}>
                    {isSelected && <FaCheck/>}
                  </XDiv>}
              </HStack>}
          </XButton>
          {canDelete &&
            <XButton onPress={() => DeleteOption(option)}>
              <MdClose color={ColorX.CSS("pureRed")}/>
            </XButton>}
        </HStack>
      );
    }else{
      return (
        <HStack key={v1()}>
          <XButton key={val} padding={5} notInjectColor round={0} colorTransition
            fullWidth bgColor={ColorX.CSS(isSelected? "aliceblue" : (_bgColor ?? bgColor ?? "pureWhite"))}
            bW={_isHighlighted ? 2 : 0} bColor={_isHighlighted ? ColorX.CSS("cmBlue") : undefined}
            onPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onListConfirm(val, _newForm);
            }} style={btnStyle} disabled={_disabled}>
            {renderOption?.(option, isSelected, _searchText) ?? 
              <HStack>
                <XText fontSize={listFontSize} textAlign="|" flexGrow={1}
                  color={_disabled ? ColorX.CSS("greyOut", 0.5) : _color}>
                  {str}
                </XText>
                {multiple && 
                  <XDiv width={20}>
                    {isSelected && <FaCheck/>}
                  </XDiv>}
              </HStack>}
          </XButton>
          {canDelete &&
            <XButton onPress={() => DeleteOption(option)}>
              <MdClose color={ColorX.CSS("pureRed")}/>
            </XButton>}
        </HStack>
      );
    }
  };

  const onBlur = (e) => {
    setShowSearch(false);
  };

  const _searchComparer = (option, _searchText) => {
    let oStr = ZSearch.GetAllLangValues(Accessor.Get(option, capAccessor));
    let show = oStr.includes(_searchText?.toLowerCase?.());
    return show;
  };

  const onEnterKeyDown = (option, _newForm) => {
    let val = Accessor.Get(option, valAccessor);
    onListConfirm(val, _newForm);
  };

  const onEscapeKeyDown = () => {
    setShowSearch(false);
    GFunc.CloseSelectorOverlay();
  };

  const openList = (searchValue = "", _newForm = undefined) => {
    if(readOnly) return;
    if(allowSearch) {
      if(!showSearch) setShowSearch(true);
    }
    setTimeout(() => textRef?.current?.focus?.(), 100);
    let rect = ref?.current?.getBoundingClientRect?.() ?? {};
    let posStyle = {};
    switch(popupPosition){
      default: case "bottom": 
        posStyle = {
          top: rect?.bottom,
          left: rect?.left,
        };
        break;
      case "top":
        posStyle = {
          bottom: window.innerHeight - (rect?.top ?? 0),
          left: rect?.left,
        };
        break;
    }

    let _options = options;
    if(allowSearch && !_.isEmpty(searchValue)){
      _options = _.filter(options, o => searchComparer?.(o, searchValue) 
        ?? _searchComparer?.(o, searchValue));
    } 

    if(hideIfDisabled){
      _options = _.filter(_options, o => !(Accessor.Get(o, disableAccessor) ?? false));
    }

    GFunc.SelectorOverlay(() => {
      return (
        <OptionList
          ctrls={opctrls}
          rect={rect} posStyle={posStyle} popupPosition={popupPosition}
          listMaxHeight={listMaxHeight} listFontSize={listFontSize}
          popupRound={popupRound} bgColor={btnColor ?? ColorX.CSS("pureWhite")}
          options={_options} valAccessor={valAccessor} disableAccessor={disableAccessor}
          multiple={multiple}
          searchValue={searchValue} _value={Accessor.Get(_newForm ?? formValue, name)}
          _newForm={_newForm ?? formValue} _renderOption={_renderOption}
          onEnterKeyDown={onEnterKeyDown}
          onEscapeKeyDown={onEscapeKeyDown}/>
      );
    }, {}, true, false, true, onBlur, true);
  };

  let {width, height = 30, placeholder} = others;
  let _value = Accessor.Get(formValue, name);
  let _option = FindOption(_value);
  let _error = !valid;
  if(isError){
    _error = _error || isError({doc: formValue, field: _value, addOns});
  }
  if(!_.isEmpty(validators)){
    _error = _error || !ZValidate.Check(_value, validators);
  }

  let _color = Accessor.Get(_option, colorAccessor) ?? color;
  let _bgColor = Accessor.Get(_option, bgColorAccessor) ?? bgColor;
  let _dColor = Accessor.Get(_option, colorAccessor) ?? disabledColor;

  const renderOneBullet = (option, idx) => {
    let cap = Accessor.Get(option, capAccessor);
    let val = Accessor.Get(option, valAccessor);
    return (
      <HStack fitWidthToContent key={idx} round={50} pL={10} pR={readOnly ? 10 : 0} m={5} bgColor={ColorX.CSS("aliceblue")} spacing={5}>
        <XText fontSize={12}>
          {LocaleX.Parse(cap)}
        </XText>
        {!readOnly && <XButton p={5} round={50} onPress={() => onListConfirm(val, undefined, false)}>
          <MdClose/>
        </XButton>}
      </HStack>
    );
  };

  const onMountedBullet = (callbacks) => {
    bulletRef.current = callbacks;
  };

  const renderBullets = () => {
    return (
      <XScroll fullWidth maxHeight={bulletHeight} onMounted={onMountedBullet}>
        <HStack flexWrap="wrap" flexShrink={0} HAlign="[">
          {_.map(_option, (o, i) => {
            return renderOneBullet(o, i);
          })}
        </HStack>
      </XScroll>
    );
  };

  const renderBlur = () => {
    const _thiscolor = ((multiple && showBullets) || _.isEmpty(display)) ? ColorX.CSS("placeholder") : (readOnly ? (_dColor ?? _color ?? ColorX.CSS("greyOut")) : _color);
    return (
      <XButton style={{display: showSearch? "none" : undefined}}
        fullWidth fullHeight bgColor={_bgColor} colorTransition
        padding={0} pL={10} notInjectColor
        tooltip={multiple ? `(${_value?.length ?? 0}) ${display}` : display}
        onPress={() => openList("")} disabled={readOnly}>
        <HStack HAlign={_.isEmpty(display) ? "[" : displayAlign} fullHeight>
          <XText fontSize={fontSize ?? 14} ellipsisMode="ellipsis" breakLines={breakLines}
            color={_thiscolor}>
            {(multiple && showBullets) ? LocaleX.Parse({EN: "Please Select...", TC: "請選擇..."}) 
              : (_.isEmpty(display) ? LocaleX.Parse(placeholder ?? {
                EN: "Please Select...",
                TC: "請選擇..."
              }) : display)}
          </XText>
        </HStack>
      </XButton>
    );
  };

  const renderFocus = () => {
    if(!allowSearch) return;
    return (
      <QFText 
        colorTransition
        disabled_bgColor={disabled_bgColor ?? ColorX.CSS("bgGreyOut")}
        disabled_color={disabledColor ?? ColorX.CSS("greyOut")}
        readOnly={readOnly}
        forwardRef={textRef}
        color={valid ? (_option?.color ?? color) : (showSearch? color: invalidColor)}
        name={showSearch ? "search" : name}
        value={showSearch ? searchText : display}
        fontSize={fontSize ?? 14}
        _onChange={showSearch ? _searchChange : null}
        _onSetDefault={_onSetDefault}
        onFocus={() => openList()}
        // onBlur={() => onBlur()}
        {...others}
        style={{
          ...others?.style,
          ...qfTextStyle,
          width: showSearch ? "100%" : 0,
          visibility: showSearch ? "visible" : "hidden",
          padding: showSearch ? undefined : 0,
          paddingLeft: showSearch ? undefined : 0,
          paddingRight: showSearch ? undefined : 0,
          paddingTop: showSearch ? undefined : 0,
          paddingBottom: showSearch ? undefined : 0, //override base settings
        }}
        placeholder={showSearch ? 
          LocaleX.Parse({EN: "Search Here...", TC: "輸入捜尋..."}) : placeholder}
        bW={0}
        />
    );
  };

  const _showBullets = multiple && showBullets;
  const _showSelector = !(multiple && readOnly && showBullets);

  return (
    <XDiv width={width} fitHeightToContent
      position="relative" overflow="visible" forwardRef={ref} {...exOthers} >
      <VStack bgColor={readOnly ? 
        (disabled_bgColor ?? bgColor ?? ColorX.CSS("bgGreyOut")) :
        (bgColor ?? ColorX.CSS("pureWhite"))} 
        bW={bW} bColor={!readOnly && _error ? error_bColor : bColor} round={round} fullWidth fullHeight 
        style={containerStyle}
        colorTransition>
        {_showBullets &&
          renderBullets()}
        {_showBullets && _showSelector &&
          <XDiv height={1} bgColor={ColorX.CSS("lightGrey", 0.5)} />}
        {_showSelector &&
          <HStack flexShrink={0} pL={3}>
            {renderBlur()}
            {renderFocus()}
            {!_.isEmpty(searchText) && canAdd &&
              <XButton lockWidth={50} round={25} padding={0} mR={5}
                style={{display: showSearch? undefined : "none"}}
                bgColor={ColorX.CSS("cmBlue")} onPress={() => AddOption(searchText)}
                color={ColorX.CSS("pureWhite")}>
                <HStack>
                  <XText>
                    {LocaleX.Parse({
                      EN: "Add",
                      TC: "增加"
                    })}
                  </XText>
                </HStack>
              </XButton>
            }
            <XButton padding={5} lockWidth={height} lockHeight={height}  delayAfterPress={100}
              disabled={readOnly} colorTransition
              onPress={() => openList("")}>
              {popupPosition === "bottom" ?
              <FaChevronDown color={arrowColor} size={iconSize}/>
              : <FaChevronUp color={arrowColor} size={iconSize}/>}
            </XButton>
          </HStack>}
      </VStack>
    </XDiv>
  );
};

export default React.memo(QFFreeTextTag, (prevProps, nextProps) => {
  let propIdentical = Accessor.IsIdentical(nextProps, prevProps);
  if(propIdentical){ return true; }

  if((prevProps?.options?.length ?? 0) !== (nextProps?.options?.length ?? 0)){
    return false;
  }
  
  let {updateVia, updateField, forceUpdateField, performanceBoost, name} = nextProps ?? {};

  if(updateVia !== "update") return false;

  if(performanceBoost){
    if(name.includes(updateField)){
        return false;
      }

    if(forceUpdateField?.length > 0 && LogicX.includesOneOf(name, forceUpdateField)){
      return false;
    }

    return true;
  }
  
  return false;
  
});;